import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { alertToast } from "@/contexts/AlertToastContext";
import { z } from "zod";
import { Eye, EyeOff, Gift, Mail, CheckCircle } from "lucide-react";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import { PasswordInput } from "@/components/ui/password-input";
import PageTransition from "@/components/PageTransition";
import { SEO } from "@/components/SEO";
import authImage from "@/assets/auth-jewelry-new.jpg";

const signupSchema = z.object({
  email: z.string().email("כתובת אימייל לא תקינה"),
  password: z.string()
    .min(8, "הסיסמה חייבת להכיל לפחות 8 תווים")
    .regex(/[0-9]/, "הסיסמה חייבת להכיל לפחות ספרה אחת")
    .regex(/[a-z]/, "הסיסמה חייבת להכיל לפחות אות קטנה אחת")
    .regex(/[A-Z]/, "הסיסמה חייבת להכיל לפחות אות גדולה אחת")
    .regex(/[!-\/:-@[-`{-~]/, "הסיסמה חייבת להכיל לפחות תו מיוחד אחד"),
  firstName: z.string().min(2, "שם פרטי חייב להכיל לפחות 2 תווים"),
  lastName: z.string().min(2, "שם משפחה חייב להכיל לפחות 2 תווים"),
  phone: z.string().min(9, "מספר טלפון חייב להכיל לפחות 9 ספרות"),
});

const loginSchema = z.object({
  email: z.string().email("כתובת אימייל לא תקינה"),
  password: z.string().min(6, "הסיסמה חייבת להכיל לפחות 6 תווים"),
});

const resetPasswordSchema = z.object({
  email: z.string().email("כתובת אימייל לא תקינה"),
});

// Google Icon component with brand colors
const GoogleIcon = () => (
  <svg className="ml-2 h-5 w-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const Auth = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  
  // Signup form
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  
  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  
  // Reset password
  const [resetEmail, setResetEmail] = useState("");
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  
  // Update password (after clicking email link)
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // Password visibility toggles
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState("");

  const getRedirectUrl = () => {
    const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
    if (savedRedirect) {
      sessionStorage.removeItem('redirectAfterLogin');
      return savedRedirect;
    }
    return '/profile';
  };

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      // Check if we're in password reset mode
      const urlParams = new URLSearchParams(window.location.search);
      const isReset = urlParams.get('reset') === 'true';
      
      if (isReset && session) {
        setIsResetMode(true);
        return;
      }
      
      if (session && !isReset) {
        navigate(getRedirectUrl());
      }
    };
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsResetMode(true);
      } else if (session && !isResetMode) {
        navigate(getRedirectUrl());
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, isResetMode]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const validatedData = signupSchema.parse({
        email: signupEmail,
        password: signupPassword,
        firstName,
        lastName,
        phone,
      });

      const redirectUrl = `${window.location.origin}/auth`;
      
      const { data, error } = await supabase.auth.signUp({
        email: validatedData.email,
        password: validatedData.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: validatedData.firstName,
            last_name: validatedData.lastName,
            phone: validatedData.phone,
          },
        },
      });

      if (error) {
        if (error.message.includes("already registered")) {
          alertToast.error("המשתמש כבר רשום במערכת");
        } else {
          alertToast.error(error.message);
        }
        return;
      }

      if (data.user) {
        // Send new customer data to webhook
        try {
          const { data: funcData, error: funcError } = await supabase.functions.invoke('send-new-customer', {
            body: {
              firstName: validatedData.firstName,
              lastName: validatedData.lastName,
              phone: validatedData.phone,
              email: validatedData.email,
            }
          });
          
          if (funcError) {
            console.error('Error sending customer data to webhook:', funcError);
          }
        } catch (webhookError) {
          console.error('Webhook error:', webhookError);
        }
        
        setConfirmationEmail(validatedData.email);
        setShowEmailConfirmation(true);
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          alertToast.error(err.message);
        });
      } else {
        alertToast.error("אירעה שגיאה בהרשמה");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const validatedData = loginSchema.parse({
        email: loginEmail,
        password: loginPassword,
      });

      const { data, error } = await supabase.auth.signInWithPassword({
        email: validatedData.email,
        password: validatedData.password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          alertToast.error("אימייל או סיסמה שגויים");
        } else {
          alertToast.error(error.message);
        }
        return;
      }

      if (data.user) {
        // Check if user is active
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('is_active')
          .eq('id', data.user.id)
          .single();

        if (profileError) {
          console.error('Error checking user status:', profileError);
        }

        if (profileData && profileData.is_active === false) {
          // User is disabled, sign them out
          await supabase.auth.signOut();
          alertToast.error("החשבון שלך הושבת. אנא פנה לתמיכה.");
          return;
        }

        alertToast.success("התחברת בהצלחה!");
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          alertToast.error(err.message);
        });
      } else {
        alertToast.error("אירעה שגיאה בהתחברות");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
      const redirectPath = savedRedirect || '/profile';
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}${redirectPath}`,
        },
      });

      if (error) {
        alertToast.error("שגיאה בהתחברות עם Google: " + error.message);
      }
    } catch (error) {
      alertToast.error("אירעה שגיאה בהתחברות עם Google");
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoading(true);

    try {
      const validatedData = resetPasswordSchema.parse({
        email: resetEmail,
      });

      const { error } = await supabase.auth.resetPasswordForEmail(validatedData.email, {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      });

      if (error) {
        alertToast.error("שגיאה בשליחת המייל: " + error.message);
        return;
      }

      alertToast.success("נשלח מייל לאיפוס סיסמה! בדוק את תיבת הדואר שלך.");
      setIsResetDialogOpen(false);
      setResetEmail("");
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          alertToast.error(err.message);
        });
      } else {
        alertToast.error("אירעה שגיאה בשליחת המייל");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      alertToast.error("הסיסמאות אינן תואמות");
      return;
    }
    
    if (newPassword.length < 6) {
      alertToast.error("הסיסמה חייבת להכיל לפחות 6 תווים");
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        alertToast.error("שגיאה בעדכון הסיסמה: " + error.message);
        return;
      }

      alertToast.success("הסיסמה עודכנה בהצלחה!");
      setIsResetMode(false);
      navigate("/");
    } catch (error) {
      alertToast.error("אירעה שגיאה בעדכון הסיסמה");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageTransition>
      <SEO
        title="התחברות והרשמה"
        description="התחבר או הירשם לחשבון באליס תכשיטים - גישה להזמנות, מועדפים, מעקב תיקונים והטבות בלעדיות"
        canonical="https://alice.jewelry/auth"
        noindex={true}
      />
      <div className="min-h-screen flex flex-col bg-background" dir="rtl">
        <Header />
        
        <div className="flex-1 flex">
          {/* Right side - Form */}
          <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 bg-gradient-to-br from-background via-muted/20 to-muted/50">
            <div className="w-full max-w-md animate-fade-slide-in">
              {showEmailConfirmation ? (
                // Email Confirmation Screen
                <div className="border border-border p-8 text-center">
                  <div className="flex justify-center mb-6">
                    <div className="bg-primary/10 p-5 rounded-full">
                      <Mail className="h-12 w-12 text-primary" />
                    </div>
                  </div>
                  <h1 className="text-2xl font-light text-foreground mb-3">בדוק את תיבת המייל שלך</h1>
                  <p className="text-muted-foreground mb-2">
                    שלחנו מייל אימות לכתובת:
                  </p>
                  <p className="text-foreground font-medium text-lg mb-6 dir-ltr" dir="ltr">
                    {confirmationEmail}
                  </p>
                  <div className="bg-muted/50 rounded-lg p-4 mb-6 text-sm text-muted-foreground space-y-2">
                    <div className="flex items-center gap-2 justify-center">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span>לחץ על הקישור במייל כדי לאשר את החשבון</span>
                    </div>
                    <p>לא קיבלת? בדוק בתיקיית הספאם</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowEmailConfirmation(false)}
                    className="w-full"
                  >
                    חזרה לדף ההתחברות
                  </Button>
                </div>
              ) : isResetMode ? (
                // Password Reset Form
                <div className="border border-border p-8">
                  <div className="text-center mb-8">
                    <h1 className="text-2xl font-light text-foreground mb-2">הזן סיסמה חדשה</h1>
                    <p className="text-muted-foreground">בחר סיסמה חדשה לחשבון שלך</p>
                  </div>
                  
                  <form onSubmit={handleUpdatePassword} className="space-y-6">
                    <PasswordInput
                      id="new-password"
                      value={newPassword}
                      onChange={setNewPassword}
                      label="סיסמה חדשה"
                      placeholder="••••••"
                      required
                      showStrength={true}
                    />
                    
                    <PasswordInput
                      id="confirm-password"
                      value={confirmPassword}
                      onChange={setConfirmPassword}
                      label="אימות סיסמה"
                      placeholder="••••••"
                      required
                      showStrength={false}
                    />
                    
                    <Button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                      disabled={isLoading}
                    >
                      {isLoading ? "מעדכן..." : "עדכן סיסמה"}
                    </Button>
                  </form>
                </div>
              ) : (
                // Login/Signup Forms
                <div className="p-8">
                  {/* Hidden for visual but kept for SEO/accessibility */}
                  <div className="sr-only">
                    <h1>תכשיטי אליס</h1>
                    <p>התחבר או הירשם לחשבון שלך</p>
                  </div>
                  
                  <Tabs defaultValue="login" dir="rtl" className="w-full">
                    <TabsList className="w-full mb-8 justify-center gap-8">
                      <TabsTrigger value="login">התחברות</TabsTrigger>
                      <TabsTrigger value="signup">הרשמה</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="login">
                      <form onSubmit={handleLogin} className="space-y-5">
                        <div className="space-y-2">
                          <Label htmlFor="login-email">אימייל</Label>
                          <Input
                            id="login-email"
                            type="email"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            placeholder="your@email.com"
                            required
                            className="text-right"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <Label htmlFor="login-password">סיסמה</Label>
                            <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
                              <DialogTrigger asChild>
                                <button
                                  type="button"
                                  className="text-sm text-primary hover:underline"
                                >
                                  שכחתי סיסמה
                                </button>
                              </DialogTrigger>
                              <DialogContent dir="rtl">
                                <DialogHeader>
                                  <DialogTitle>איפוס סיסמה</DialogTitle>
                                  <DialogDescription>
                                    הזן את כתובת המייל שלך ונשלח לך קישור לאיפוס הסיסמה
                                  </DialogDescription>
                                </DialogHeader>
                                <form onSubmit={handleResetPassword} className="space-y-4">
                                  <div className="space-y-2">
                                    <Label htmlFor="reset-email">אימייל</Label>
                                    <Input
                                      id="reset-email"
                                      type="email"
                                      value={resetEmail}
                                      onChange={(e) => setResetEmail(e.target.value)}
                                      placeholder="your@email.com"
                                      required
                                      className="text-right"
                                    />
                                  </div>
                                  <Button
                                    type="submit"
                                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                                    disabled={isLoading}
                                  >
                                    {isLoading ? "שולח..." : "שלח קישור לאיפוס סיסמה"}
                                  </Button>
                                </form>
                              </DialogContent>
                            </Dialog>
                          </div>
                          <div className="relative">
                            <Input
                              id="login-password"
                              type={showLoginPassword ? "text" : "password"}
                              value={loginPassword}
                              onChange={(e) => setLoginPassword(e.target.value)}
                              placeholder="••••••"
                              required
                              className="text-right pl-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowLoginPassword(!showLoginPassword)}
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        
                        <Button
                          type="submit"
                          className="w-full py-6 bg-primary hover:bg-primary/90 text-primary-foreground text-base font-medium tracking-wide transition-all hover:shadow-lg"
                          disabled={isLoading}
                        >
                          {isLoading ? "מתחבר..." : "התחבר"}
                        </Button>
                        
                        <div className="relative my-6">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-border" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-background px-2 text-muted-foreground">או</span>
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          className="w-full py-6 border-border hover:border-primary/50 hover:bg-primary/5 hover:text-foreground transition-all"
                          onClick={handleGoogleLogin}
                          disabled={isLoading}
                        >
                          <GoogleIcon />
                          התחבר באמצעות גוגל
                        </Button>
                      </form>
                    </TabsContent>
                    
                    <TabsContent value="signup">
                      <form onSubmit={handleSignup} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="first-name">שם פרטי</Label>
                            <Input
                              id="first-name"
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              placeholder="שם פרטי"
                              required
                              className="text-right"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="last-name">שם משפחה</Label>
                            <Input
                              id="last-name"
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              placeholder="שם משפחה"
                              required
                              className="text-right"
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="phone">טלפון</Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="05X-XXXXXXX"
                            required
                            className="text-right"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="signup-email">אימייל</Label>
                          <Input
                            id="signup-email"
                            type="email"
                            value={signupEmail}
                            onChange={(e) => setSignupEmail(e.target.value)}
                            placeholder="your@email.com"
                            required
                            className="text-right"
                          />
                        </div>
                        
                        <PasswordInput
                          id="signup-password"
                          value={signupPassword}
                          onChange={setSignupPassword}
                          label="סיסמה"
                          placeholder="••••••"
                          required
                          showStrength={true}
                        />
                        
                        {/* Coupon Banner for new customers */}
                        <div className="bg-gradient-to-l from-primary/10 to-primary/5 border border-primary/20 p-4 text-center">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <Gift className="w-5 h-5 text-primary" />
                            <span className="font-semibold text-primary">מתנה ללקוחות חדשים!</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            הירשמו וקבלו קוד קופון של 15% הנחה לרכישה הראשונה
                          </p>
                        </div>
                        
                        <Button
                          type="submit"
                          className="w-full py-6 bg-primary hover:bg-primary/90 text-primary-foreground text-base font-medium tracking-wide transition-all hover:shadow-lg"
                          disabled={isLoading}
                        >
                          {isLoading ? "נרשם..." : "הירשם"}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>
                  
                  <div className="mt-8 pt-6 border-t border-border text-center text-sm text-muted-foreground">
                    <Link to="/terms" className="text-primary hover:text-primary/80 transition-colors">תנאי שימוש</Link>
                    <span className="mx-3 text-border">|</span>
                    <Link to="/privacy" className="text-primary hover:text-primary/80 transition-colors">מדיניות פרטיות</Link>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Left side - Hero Image */}
          <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
            <img 
              src={authImage} 
              alt="תכשיטי אליס" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Subtle overlay */}
            <div className="absolute inset-0 bg-black/20" />
          </div>
        </div>
        
        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default Auth;
